import { Fragment, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  FileDown,
  KeyRound,
  LogOut,
  Pill,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Search,
  Trash2,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabaseClient } from '../lib/supabaseClient';
import { compareResidentsByKana, hasResidentKana } from '../lib/residentSort';

type MedicineResident = {
  id: string;
  resident_key: string;
  display_order: number;
  name: string;
  kana: string | null;
  room: string | null;
  last_visit_date: string | null;
  next_visit_date: string | null;
  visit_interval_days: number;
  active: boolean;
  notes: string | null;
};

type MedicineDrug = {
  id: string;
  drug_name: string;
  default_unit: string;
};

type MedicationType = 'normal' | 'temporary' | 'prn' | 'custom';
type PrescriptionSource = 'home_visit' | 'external_1' | 'external_2' | 'external_3';
type ExternalVisitType = 'regular' | 'temporary';

type ResidentMedication = {
  id: string;
  resident_id: string;
  drug_id: string | null;
  drug_name: string;
  drug_name_snapshot: string;
  medication_type: MedicationType;
  prescription_source: PrescriptionSource;
  external_visit_type: ExternalVisitType | null;
  external_label: string | null;
  hospital_name: string | null;
  external_hospital_name: string | null;
  external_end_date: string | null;
  dosage_text: string | null;
  timing_text: string | null;
  temporary_dates_text: string | null;
  daily_use: number;
  stock_unit: string;
  current_stock: number;
  stock_saved_at: string;
  stock_confirmed: boolean;
  order_threshold_days: number;
  days_supply: number | null;
  sort_order: number;
  active: boolean;
  notes: string | null;
};

type MedicineSnapshot = {
  residents: MedicineResident[];
  drugMaster: MedicineDrug[];
  medications: ResidentMedication[];
};

type MedicationEvaluation = ResidentMedication & {
  estimatedStock: number;
  elapsedDays: number;
  daysLeft: number | null;
  status: 'request' | 'ok' | 'manual' | 'unconfirmed' | 'inactive' | 'ended';
};

type DrugMasterSummary = {
  id: string;
  drugName: string;
  defaultUnit: string;
  activeMedicationCount: number;
  residentCount: number;
  residentNames: string[];
};

type MedicationGroup = {
  key: string;
  source: PrescriptionSource;
  hospitalName: string | null;
  externalLabel: string | null;
  title: string;
  medications: MedicationEvaluation[];
  requestCount: number;
  unconfirmedCount: number;
  endedCount: number;
};

type ResidentDraft = {
  id?: string;
  resident_key?: string;
  display_order: number;
  name: string;
  kana: string;
  room: string;
  last_visit_date: string;
  next_visit_date: string;
  visit_interval_days: number;
  notes: string;
  active: boolean;
};

type MedicationDraft = {
  id?: string;
  resident_id: string;
  drug_name: string;
  use_custom_drug_name: boolean;
  medication_type: MedicationType;
  prescription_source: PrescriptionSource;
  external_visit_type: ExternalVisitType | '';
  external_label: string;
  hospital_name: string;
  external_hospital_name: string;
  external_end_date: string;
  dosage_text: string;
  timing_text: string;
  temporary_dates_text: string;
  daily_use: number;
  stock_unit: string;
  current_stock: number;
  stock_confirmed: boolean;
  order_threshold_days: number;
  days_supply: string;
  sort_order: number;
  notes: string;
  active: boolean;
};

const SESSION_KEY = 'satsuki.residents.session.v1';
const LEGACY_SESSION_KEY = 'satsuki.medicine.session.v1';
const DAY_MS = 24 * 60 * 60 * 1000;
const STOCK_UNITS = ['錠', 'シート', '包', 'g', 'mg', 'ml', 'cap', '個', '滴', '枚', '本', '回', '単位'];

const medicationTypeLabels: Record<MedicationType, string> = {
  normal: '通常',
  temporary: '一時',
  prn: '頓服',
  custom: 'その他'
};

const prescriptionSourceMeta: Record<PrescriptionSource, {
  label: string;
  badgeClass: string;
  rowClass: string;
  leftBorderClass: string;
  groupHeaderClass: string;
  groupBadgeClass: string;
}> = {
  home_visit: {
    label: '往診医',
    badgeClass: 'bg-teal-700 text-white ring-1 ring-teal-800',
    rowClass: 'bg-teal-50/30',
    leftBorderClass: 'border-l-teal-500',
    groupHeaderClass: 'border-teal-200 bg-teal-50 text-teal-950',
    groupBadgeClass: 'bg-teal-700 text-white'
  },
  external_1: {
    label: '外部受診①',
    badgeClass: 'bg-sky-700 text-white ring-1 ring-sky-800',
    rowClass: 'bg-sky-50/60',
    leftBorderClass: 'border-l-sky-500',
    groupHeaderClass: 'border-sky-200 bg-sky-50 text-sky-950',
    groupBadgeClass: 'bg-sky-700 text-white'
  },
  external_2: {
    label: '外部受診②',
    badgeClass: 'bg-violet-700 text-white ring-1 ring-violet-800',
    rowClass: 'bg-violet-50/60',
    leftBorderClass: 'border-l-violet-500',
    groupHeaderClass: 'border-violet-200 bg-violet-50 text-violet-950',
    groupBadgeClass: 'bg-violet-700 text-white'
  },
  external_3: {
    label: '外部受診③',
    badgeClass: 'bg-orange-600 text-white ring-1 ring-orange-700',
    rowClass: 'bg-orange-50/70',
    leftBorderClass: 'border-l-orange-500',
    groupHeaderClass: 'border-orange-200 bg-orange-50 text-orange-950',
    groupBadgeClass: 'bg-orange-600 text-white'
  }
};

const externalVisitTypeLabels: Record<ExternalVisitType, string> = {
  regular: '定期',
  temporary: '臨時'
};

const hospitalColorPalette = [
  {
    badgeClass: 'bg-cyan-100 text-cyan-950 ring-1 ring-cyan-300',
    strongBadgeClass: 'bg-cyan-700 text-white',
    headerClass: 'border-cyan-200 bg-cyan-50 text-cyan-950'
  },
  {
    badgeClass: 'bg-fuchsia-100 text-fuchsia-950 ring-1 ring-fuchsia-300',
    strongBadgeClass: 'bg-fuchsia-700 text-white',
    headerClass: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-950'
  },
  {
    badgeClass: 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-300',
    strongBadgeClass: 'bg-emerald-700 text-white',
    headerClass: 'border-emerald-200 bg-emerald-50 text-emerald-950'
  },
  {
    badgeClass: 'bg-rose-100 text-rose-950 ring-1 ring-rose-300',
    strongBadgeClass: 'bg-rose-700 text-white',
    headerClass: 'border-rose-200 bg-rose-50 text-rose-950'
  },
  {
    badgeClass: 'bg-indigo-100 text-indigo-950 ring-1 ring-indigo-300',
    strongBadgeClass: 'bg-indigo-700 text-white',
    headerClass: 'border-indigo-200 bg-indigo-50 text-indigo-950'
  },
  {
    badgeClass: 'bg-lime-100 text-lime-950 ring-1 ring-lime-300',
    strongBadgeClass: 'bg-lime-700 text-white',
    headerClass: 'border-lime-200 bg-lime-50 text-lime-950'
  }
];

const missingHospitalColor = {
  badgeClass: 'bg-amber-100 text-amber-950 ring-1 ring-amber-300',
  strongBadgeClass: 'bg-amber-600 text-white',
  headerClass: 'border-amber-200 bg-amber-50 text-amber-950'
};

const prescriptionSourceOrder: Record<PrescriptionSource, number> = {
  home_visit: 0,
  external_1: 1,
  external_2: 2,
  external_3: 3
};

const medicationStatusOrder: Record<MedicationEvaluation['status'], number> = {
  request: 0,
  unconfirmed: 1,
  ended: 2,
  manual: 3,
  ok: 4,
  inactive: 5
};

const formatNumber = (value: number, fractionDigits = 0) =>
  new Intl.NumberFormat('ja-JP', {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits
  }).format(value);

const normalizeNumber = (value: unknown, fallback = 0): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toDateInput = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.slice(0, 10);
};

const normalizePrescriptionSource = (value: unknown): PrescriptionSource => {
  return value === 'external_1' || value === 'external_2' || value === 'external_3' ? value : 'home_visit';
};

const normalizeExternalVisitType = (value: unknown): ExternalVisitType | null => {
  return value === 'regular' || value === 'temporary' ? value : null;
};

const isExternalSource = (source: PrescriptionSource | null | undefined): boolean => source !== undefined && normalizePrescriptionSource(source) !== 'home_visit';

const isPastDate = (value: string | null | undefined): boolean => {
  if (!value) return false;
  const daysUntil = getDaysUntil(value);
  return daysUntil !== null && daysUntil < 0;
};

const hashText = (value: string): number => {
  let hash = 0;
  Array.from(value).forEach(char => {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  });
  return hash;
};

const getHospitalName = (medication: Pick<ResidentMedication, 'prescription_source' | 'hospital_name' | 'external_hospital_name'>): string => {
  const source = normalizePrescriptionSource(medication.prescription_source);
  const hospitalName = medication.hospital_name?.trim() || medication.external_hospital_name?.trim();
  if (hospitalName) return hospitalName;
  return source === 'home_visit' ? '往診医療機関未設定' : '病院未設定';
};

const getHospitalColor = (hospitalName: string | null | undefined) => {
  const name = hospitalName?.trim();
  if (!name || name === '病院未設定' || name === '往診医療機関未設定') return missingHospitalColor;
  return hospitalColorPalette[hashText(name) % hospitalColorPalette.length];
};

const formatMedicationHospital = (medication: Pick<ResidentMedication, 'prescription_source' | 'hospital_name' | 'external_hospital_name'>): string => {
  return getHospitalName(medication);
};

const formatMedicationSource = (medication: Pick<ResidentMedication, 'prescription_source' | 'external_visit_type' | 'external_label' | 'hospital_name' | 'external_hospital_name' | 'external_end_date'>): string => {
  const source = normalizePrescriptionSource(medication.prescription_source);
  if (source === 'home_visit') return prescriptionSourceMeta.home_visit.label;

  const parts = [prescriptionSourceMeta[source].label];
  if (medication.external_label) parts.push(medication.external_label);
  if (medication.external_visit_type) parts.push(externalVisitTypeLabels[medication.external_visit_type]);
  if (medication.external_end_date) parts.push(`終了 ${medication.external_end_date}`);
  return parts.join(' / ');
};

const formatMedicationVisitSummary = (medication: Pick<ResidentMedication, 'prescription_source' | 'external_visit_type' | 'external_label' | 'hospital_name' | 'external_hospital_name' | 'external_end_date'>): string => {
  const source = normalizePrescriptionSource(medication.prescription_source);
  if (source === 'home_visit') return prescriptionSourceMeta.home_visit.label;
  return `${formatMedicationHospital(medication)} / ${formatMedicationSource(medication)}`;
};

const getMedicationGroupTitle = (medication: Pick<ResidentMedication, 'prescription_source' | 'external_label' | 'hospital_name' | 'external_hospital_name'>): string => {
  const source = normalizePrescriptionSource(medication.prescription_source);
  const parts = [prescriptionSourceMeta[source].label, getHospitalName(medication)];
  if (medication.external_label) parts.push(medication.external_label);
  return parts.join(' / ');
};

const getMedicationGroupKey = (medication: Pick<ResidentMedication, 'prescription_source' | 'external_label' | 'hospital_name' | 'external_hospital_name'>): string => {
  const source = normalizePrescriptionSource(medication.prescription_source);
  const hospital = getHospitalName(medication);
  const label = source === 'home_visit' ? '' : medication.external_label?.trim() ?? '';
  return `${source}:${hospital}:${label}`;
};

const sortMedicationsForCare = (a: MedicationEvaluation, b: MedicationEvaluation): number => {
  const statusDiff = medicationStatusOrder[a.status] - medicationStatusOrder[b.status];
  if (statusDiff !== 0) return statusDiff;

  const aEnd = a.external_end_date ? getDaysUntil(a.external_end_date) ?? 99999 : 99999;
  const bEnd = b.external_end_date ? getDaysUntil(b.external_end_date) ?? 99999 : 99999;
  if (aEnd !== bEnd) return aEnd - bEnd;

  return a.sort_order - b.sort_order || a.drug_name.localeCompare(b.drug_name, 'ja');
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getElapsedDays = (savedAt: string): number => {
  const saved = new Date(savedAt).getTime();
  if (!Number.isFinite(saved)) return 0;
  return Math.max(0, Math.floor((Date.now() - saved) / DAY_MS));
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getNextVisitDate = (resident: MedicineResident): string | null => {
  if (resident.next_visit_date) return resident.next_visit_date;
  if (!resident.last_visit_date) return null;
  const base = new Date(`${resident.last_visit_date}T00:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  return addDays(base, resident.visit_interval_days || 14).toISOString().slice(0, 10);
};

const getDaysUntil = (dateText: string | null): number | null => {
  if (!dateText) return null;
  const target = new Date(`${dateText}T00:00:00`).getTime();
  if (!Number.isFinite(target)) return null;
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.ceil((target - base) / DAY_MS);
};

const evaluateMedication = (medication: ResidentMedication, resident: MedicineResident | undefined): MedicationEvaluation => {
  const dailyUse = normalizeNumber(medication.daily_use);
  const elapsedDays = medication.stock_confirmed ? getElapsedDays(medication.stock_saved_at) : 0;
  const estimatedStock = medication.stock_confirmed
    ? Math.max(0, normalizeNumber(medication.current_stock) - dailyUse * elapsedDays)
    : normalizeNumber(medication.current_stock);
  const daysLeft = medication.stock_confirmed && dailyUse > 0 ? estimatedStock / dailyUse : null;
  const nextVisit = resident ? getNextVisitDate(resident) : null;
  const daysUntilVisit = getDaysUntil(nextVisit);
  const threshold = Math.max(
    normalizeNumber(medication.order_threshold_days, 14),
    daysUntilVisit === null ? 0 : Math.max(0, daysUntilVisit)
  );

  let status: MedicationEvaluation['status'] = 'ok';
  if (!medication.active) {
    status = 'inactive';
  } else if (
    isExternalSource(medication.prescription_source) &&
    medication.external_visit_type === 'temporary' &&
    isPastDate(medication.external_end_date)
  ) {
    status = 'ended';
  } else if (!medication.stock_confirmed) {
    status = 'unconfirmed';
  } else if (dailyUse <= 0) {
    status = 'manual';
  } else if (daysLeft !== null && daysLeft <= threshold) {
    status = 'request';
  }

  return {
    ...medication,
    estimatedStock,
    elapsedDays,
    daysLeft,
    status
  };
};

const emptyResidentDraft = (displayOrder: number): ResidentDraft => ({
  display_order: displayOrder,
  name: '',
  kana: '',
  room: '',
  last_visit_date: '',
  next_visit_date: '',
  visit_interval_days: 14,
  notes: '',
  active: true
});

const residentToDraft = (resident: MedicineResident): ResidentDraft => ({
  id: resident.id,
  resident_key: resident.resident_key,
  display_order: resident.display_order,
  name: resident.name,
  kana: resident.kana ?? '',
  room: resident.room ?? '',
  last_visit_date: toDateInput(resident.last_visit_date),
  next_visit_date: toDateInput(resident.next_visit_date),
  visit_interval_days: resident.visit_interval_days,
  notes: resident.notes ?? '',
  active: resident.active
});

const emptyMedicationDraft = (residentId: string, sortOrder: number): MedicationDraft => ({
  resident_id: residentId,
  drug_name: '',
  use_custom_drug_name: false,
  medication_type: 'normal',
  prescription_source: 'home_visit',
  external_visit_type: '',
  external_label: '',
  hospital_name: '',
  external_hospital_name: '',
  external_end_date: '',
  dosage_text: '',
  timing_text: '',
  temporary_dates_text: '',
  daily_use: 0,
  stock_unit: '錠',
  current_stock: 0,
  stock_confirmed: false,
  order_threshold_days: 14,
  days_supply: '',
  sort_order: sortOrder,
  notes: '',
  active: true
});

const medicationToDraft = (medication: ResidentMedication): MedicationDraft => ({
  id: medication.id,
  resident_id: medication.resident_id,
  drug_name: medication.drug_name,
  use_custom_drug_name: false,
  medication_type: medication.medication_type,
  prescription_source: normalizePrescriptionSource(medication.prescription_source),
  external_visit_type: medication.external_visit_type ?? '',
  external_label: medication.external_label ?? '',
  hospital_name: medication.hospital_name ?? medication.external_hospital_name ?? '',
  external_hospital_name: medication.external_hospital_name ?? '',
  external_end_date: toDateInput(medication.external_end_date),
  dosage_text: medication.dosage_text ?? '',
  timing_text: medication.timing_text ?? '',
  temporary_dates_text: medication.temporary_dates_text ?? '',
  daily_use: medication.daily_use,
  stock_unit: medication.stock_unit,
  current_stock: medication.current_stock,
  stock_confirmed: medication.stock_confirmed,
  order_threshold_days: medication.order_threshold_days,
  days_supply: medication.days_supply === null ? '' : String(medication.days_supply),
  sort_order: medication.sort_order,
  notes: medication.notes ?? '',
  active: medication.active
});

const MedicinePage = () => {
  const [searchParams] = useSearchParams();
  const [sessionToken, setSessionToken] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage.getItem(SESSION_KEY) ?? window.sessionStorage.getItem(LEGACY_SESSION_KEY) ?? '';
  });
  const [passcode, setPasscode] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [residents, setResidents] = useState<MedicineResident[]>([]);
  const [drugMaster, setDrugMaster] = useState<MedicineDrug[]>([]);
  const [medications, setMedications] = useState<ResidentMedication[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [drugQuery, setDrugQuery] = useState('');
  const [viewMode, setViewMode] = useState<'residents' | 'drugs'>('residents');
  const [showInactive, setShowInactive] = useState(false);
  const [residentDraft, setResidentDraft] = useState<ResidentDraft | null>(null);
  const [medicationDraft, setMedicationDraft] = useState<MedicationDraft | null>(null);
  const [stockDrafts, setStockDrafts] = useState<Record<string, string>>({});
  const [pdfExporting, setPdfExporting] = useState(false);

  const authenticated = Boolean(sessionToken);

  const residentById = useMemo(() => new Map(residents.map(resident => [resident.id, resident])), [residents]);

  const evaluatedMedications = useMemo(
    () => medications.map(medication => evaluateMedication(medication, residentById.get(medication.resident_id))),
    [medications, residentById]
  );

  const selectedResident = useMemo(
    () => residents.find(resident => resident.id === selectedResidentId) ?? residents.find(resident => resident.active) ?? residents[0],
    [residents, selectedResidentId]
  );

  const selectedMedications = useMemo(
    () =>
      evaluatedMedications
        .filter(medication => selectedResident && medication.resident_id === selectedResident.id)
        .filter(medication => showInactive || medication.active)
        .sort((a, b) => a.sort_order - b.sort_order),
    [evaluatedMedications, selectedResident, showInactive]
  );

  const selectedMedicationGroups = useMemo<MedicationGroup[]>(() => {
    const groups = new Map<string, MedicationGroup>();

    selectedMedications.forEach(medication => {
      const source = normalizePrescriptionSource(medication.prescription_source);
      const key = getMedicationGroupKey(medication);
      const existing = groups.get(key);
      const group = existing ?? {
        key,
        source,
        hospitalName: getHospitalName(medication),
        externalLabel: source === 'home_visit' ? null : medication.external_label?.trim() || null,
        title: getMedicationGroupTitle(medication),
        medications: [],
        requestCount: 0,
        unconfirmedCount: 0,
        endedCount: 0
      };

      group.medications.push(medication);
      group.requestCount += medication.status === 'request' ? 1 : 0;
      group.unconfirmedCount += medication.status === 'unconfirmed' ? 1 : 0;
      group.endedCount += medication.status === 'ended' ? 1 : 0;
      groups.set(key, group);
    });

    return Array.from(groups.values())
      .map(group => ({
        ...group,
        medications: [...group.medications].sort(sortMedicationsForCare)
      }))
      .sort((a, b) =>
        prescriptionSourceOrder[a.source] - prescriptionSourceOrder[b.source] ||
        (a.hospitalName ?? '').localeCompare(b.hospitalName ?? '', 'ja') ||
        (a.externalLabel ?? '').localeCompare(b.externalLabel ?? '', 'ja') ||
        a.title.localeCompare(b.title, 'ja')
      );
  }, [selectedMedications]);

  const requestItems = useMemo(
    () =>
      evaluatedMedications
        .filter(medication => medication.active && medication.status === 'request')
        .sort((a, b) => (a.daysLeft ?? 99999) - (b.daysLeft ?? 99999)),
    [evaluatedMedications]
  );

  const homeVisitRequestItems = useMemo(
    () => requestItems.filter(medication => !isExternalSource(medication.prescription_source)),
    [requestItems]
  );

  const externalRequestItems = useMemo(
    () => requestItems.filter(medication => isExternalSource(medication.prescription_source)),
    [requestItems]
  );

  const filteredResidents = useMemo(() => {
    const query = search.trim().toLowerCase();
    return residents
      .filter(resident => showInactive || resident.active)
      .filter(resident => {
        if (!query) return true;
        return `${resident.name} ${resident.kana ?? ''} ${resident.room ?? ''}`.toLowerCase().includes(query);
      })
      .sort(compareResidentsByKana);
  }, [residents, search, showInactive]);

  const drugMasterRows = useMemo<DrugMasterSummary[]>(() => {
    const activeEvaluations = evaluatedMedications.filter(medication => medication.active);
    const query = drugQuery.trim().toLowerCase();

    return drugMaster
      .map(drug => {
        const relatedMeds = activeEvaluations.filter(medication =>
          medication.drug_id === drug.id || medication.drug_name === drug.drug_name
        );
        const residentNames = Array.from(new Set(
          relatedMeds
            .map(medication => residentById.get(medication.resident_id)?.name)
            .filter((name): name is string => Boolean(name))
        )).sort((a, b) => a.localeCompare(b, 'ja'));
        return {
          id: drug.id,
          drugName: drug.drug_name,
          defaultUnit: drug.default_unit,
          activeMedicationCount: relatedMeds.length,
          residentCount: residentNames.length,
          residentNames
        };
      })
      .filter(summary => {
        if (!query) return true;
        return `${summary.drugName} ${summary.defaultUnit} ${summary.residentNames.join(' ')}`.toLowerCase().includes(query);
      })
      .sort((a, b) => b.activeMedicationCount - a.activeMedicationCount || a.drugName.localeCompare(b.drugName, 'ja'));
  }, [drugMaster, drugQuery, evaluatedMedications, residentById]);

  const summary = useMemo(() => {
    const activeResidents = residents.filter(resident => resident.active).length;
    const activeMeds = medications.filter(medication => medication.active).length;
    const unconfirmed = evaluatedMedications.filter(medication => medication.active && medication.status === 'unconfirmed').length;
    return {
      activeResidents,
      activeMeds,
      requestCount: homeVisitRequestItems.length,
      externalRequestCount: externalRequestItems.length,
      unconfirmed
    };
  }, [evaluatedMedications, externalRequestItems.length, homeVisitRequestItems.length, medications, residents]);

  const loadSnapshot = async (token = sessionToken) => {
    if (!supabaseClient || !token) return;
    setLoading(true);
    try {
      const { data, error } = await supabaseClient.rpc('medicine_get_snapshot', { _session_token: token });
      if (error) throw error;
      const snapshot = data as MedicineSnapshot;
      const nextResidents = snapshot.residents ?? [];
      const nextMedications = (snapshot.medications ?? []).map(medication => ({
        ...medication,
        prescription_source: normalizePrescriptionSource(medication.prescription_source),
        external_visit_type: normalizeExternalVisitType(medication.external_visit_type),
        external_label: medication.external_label ?? null,
        hospital_name: medication.hospital_name ?? medication.external_hospital_name ?? null,
        external_hospital_name: medication.external_hospital_name ?? null,
        external_end_date: medication.external_end_date ? toDateInput(medication.external_end_date) : null,
        daily_use: normalizeNumber(medication.daily_use),
        current_stock: normalizeNumber(medication.current_stock),
        order_threshold_days: normalizeNumber(medication.order_threshold_days, 14)
      }));
      setResidents(nextResidents);
      setDrugMaster(snapshot.drugMaster ?? []);
      setMedications(nextMedications);
      const requestedResidentId = searchParams.get('resident') ?? '';
      const requestedExists = nextResidents.some(resident => resident.id === requestedResidentId);
      setSelectedResidentId(prev => (requestedExists ? requestedResidentId : prev) || nextResidents.find(resident => resident.active)?.id || nextResidents[0]?.id || '');
      setStockDrafts(Object.fromEntries(nextMedications.map(medication => [medication.id, String(medication.current_stock)])));
      setAuthError(null);
    } catch (error: any) {
      if (String(error?.message ?? '').includes('認証') || error?.code === '28000') {
        handleLogout();
      }
      setAuthError(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!supabaseClient) {
      setAuthError('Supabaseの設定が見つかりません。');
      return;
    }
    setLoading(true);
    setAuthError(null);
    try {
      const { data, error } = await supabaseClient.rpc('medicine_start_session', { _passcode: passcode });
      if (error) throw error;
      const token = (data as { token: string }).token;
      setSessionToken(token);
      window.sessionStorage.setItem(SESSION_KEY, token);
      setPasscode('');
      await loadSnapshot(token);
    } catch (error: any) {
      setAuthError(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setSessionToken('');
    setResidents([]);
    setDrugMaster([]);
    setMedications([]);
    window.sessionStorage.removeItem(SESSION_KEY);
    window.sessionStorage.removeItem(LEGACY_SESSION_KEY);
  };

  useEffect(() => {
    if (sessionToken) {
      window.sessionStorage.setItem(SESSION_KEY, sessionToken);
      void loadSnapshot(sessionToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '残薬管理 | さつきホーム';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const showNotice = (message: string) => {
    setSaveNotice(message);
    window.setTimeout(() => setSaveNotice(null), 2500);
  };

  const saveResident = async () => {
    if (!supabaseClient || !sessionToken || !residentDraft) return;
    if (!residentDraft.name.trim()) {
      alert('入居者名を入力してください');
      return;
    }
    const payload = {
      ...residentDraft,
      last_visit_date: residentDraft.last_visit_date || null,
      next_visit_date: residentDraft.next_visit_date || null
    };
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('medicine_save_resident', {
        _session_token: sessionToken,
        _resident: payload
      });
      if (error) throw error;
      setResidentDraft(null);
      await loadSnapshot();
      showNotice('入居者を保存しました');
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const archiveResident = async (resident: MedicineResident) => {
    if (!supabaseClient || !sessionToken) return;
    if (!window.confirm(`${resident.name}さんを非表示にしますか？紐づく薬も非表示になります。`)) return;
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('medicine_archive_resident', {
        _session_token: sessionToken,
        _resident_id: resident.id
      });
      if (error) throw error;
      await loadSnapshot();
      showNotice('入居者を非表示にしました');
    } catch (error: any) {
      alert(`非表示に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const saveMedication = async () => {
    if (!supabaseClient || !sessionToken || !medicationDraft) return;
    if (!medicationDraft.drug_name.trim()) {
      alert('薬名を入力してください');
      return;
    }
    const source = normalizePrescriptionSource(medicationDraft.prescription_source);
    const isExternal = source !== 'home_visit';
    const payload = {
      ...medicationDraft,
      prescription_source: source,
      external_visit_type: isExternal ? medicationDraft.external_visit_type || null : null,
      external_label: isExternal ? medicationDraft.external_label.trim() || null : null,
      hospital_name: medicationDraft.hospital_name.trim() || null,
      external_hospital_name: isExternal ? medicationDraft.hospital_name.trim() || null : null,
      external_end_date: isExternal ? medicationDraft.external_end_date || null : null,
      days_supply: medicationDraft.days_supply === '' ? null : Number(medicationDraft.days_supply)
    };
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('medicine_save_medication', {
        _session_token: sessionToken,
        _medication: payload
      });
      if (error) throw error;
      setMedicationDraft(null);
      await loadSnapshot();
      showNotice('薬情報を保存しました');
    } catch (error: any) {
      alert(`保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const archiveMedication = async (medication: ResidentMedication) => {
    if (!supabaseClient || !sessionToken) return;
    if (!window.confirm(`${medication.drug_name}を終了扱いにしますか？`)) return;
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('medicine_archive_medication', {
        _session_token: sessionToken,
        _medication_id: medication.id
      });
      if (error) throw error;
      await loadSnapshot();
      showNotice('薬を終了扱いにしました');
    } catch (error: any) {
      alert(`終了処理に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const saveStock = async (medication: ResidentMedication) => {
    if (!supabaseClient || !sessionToken) return;
    const value = normalizeNumber(stockDrafts[medication.id], NaN);
    if (!Number.isFinite(value) || value < 0) {
      alert('残薬数を0以上で入力してください');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabaseClient.rpc('medicine_save_stock_count', {
        _session_token: sessionToken,
        _medication_id: medication.id,
        _current_stock: value,
        _stock_unit: medication.stock_unit,
        _memo: '画面から残薬保存'
      });
      if (error) throw error;
      await loadSnapshot();
      showNotice('残薬数を保存しました');
    } catch (error: any) {
      alert(`残薬保存に失敗しました: ${error?.message ?? String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const exportRequestCsv = () => {
    const rows = [
      ['区分', '入居者', '処方元', '医療機関', '薬名', '残薬目安', '残日数', '服用量', '服用時間', '次回往診目安'],
      ...requestItems.map(item => {
        const resident = residentById.get(item.resident_id);
        return [
          isExternalSource(item.prescription_source) ? '外部確認' : '往診依頼',
          resident?.name ?? '',
          formatMedicationSource(item),
          formatMedicationHospital(item),
          item.drug_name,
          `${formatNumber(item.estimatedStock, 1)}${item.stock_unit}`,
          item.daysLeft === null ? '' : `${formatNumber(item.daysLeft, 1)}日`,
          item.dosage_text ?? '',
          item.timing_text ?? '',
          resident ? getNextVisitDate(resident) ?? '' : ''
        ];
      })
    ];
    const csv = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `medicine-request-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportRequestPdf = async () => {
    if (pdfExporting) return;
    setPdfExporting(true);

    try {
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pageWidth = 1200;
      const pageHeight = 1697;
      const margin = 52;
      const rowPadding = 12;
      const columns = [
        { header: '区分', width: 100 },
        { header: '入居者', width: 120 },
        { header: '処方元', width: 135 },
        { header: '医療機関', width: 160 },
        { header: '薬名', width: 275 },
        { header: '残薬目安', width: 115 },
        { header: '残日数', width: 95 },
        { header: '服用量', width: 100 },
        { header: '服用時間', width: 100 }
      ];
      const tableWidth = columns.reduce((sum, column) => sum + column.width, 0);
      const rows = requestItems.length > 0
        ? requestItems.map(item => {
          const resident = residentById.get(item.resident_id);
          return [
            isExternalSource(item.prescription_source) ? '外部確認' : '往診依頼',
            resident?.name ?? '-',
            formatMedicationSource(item),
            formatMedicationHospital(item),
            item.drug_name,
            `${formatNumber(item.estimatedStock, 1)}${item.stock_unit}`,
            item.daysLeft === null ? '-' : `${formatNumber(item.daysLeft, 1)}日`,
            item.dosage_text || '-',
            item.timing_text || '-'
          ];
        })
        : [['依頼・確認候補はありません', '', '', '', '', '', '', '', '']];

      const createCanvasPage = () => {
        const canvas = document.createElement('canvas');
        canvas.width = pageWidth;
        canvas.height = pageHeight;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvasを作成できませんでした。');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, pageWidth, pageHeight);
        context.textBaseline = 'top';
        return { canvas, context };
      };

      const wrapText = (context: CanvasRenderingContext2D, text: string, maxWidth: number) => {
        const source = text || '-';
        const lines: string[] = [];
        let current = '';
        Array.from(source).forEach(char => {
          const next = `${current}${char}`;
          if (current && context.measureText(next).width > maxWidth) {
            lines.push(current);
            current = char;
          } else {
            current = next;
          }
        });
        lines.push(current || '-');
        return lines;
      };

      const strokeRect = (context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, fill?: string) => {
        if (fill) {
          context.fillStyle = fill;
          context.fillRect(x, y, width, height);
        }
        context.strokeStyle = '#cbd5e1';
        context.lineWidth = 1;
        context.strokeRect(x, y, width, height);
      };

      const drawHeader = (context: CanvasRenderingContext2D, page: number) => {
        context.fillStyle = '#0f172a';
        context.font = 'bold 30px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
        context.fillText('残薬管理 依頼・確認候補', margin, 42);
        context.font = '18px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
        context.fillStyle = '#475569';
        context.fillText(`出力日: ${new Date().toLocaleDateString('ja-JP')}`, margin, 88);
        context.fillText(`ページ ${page}`, pageWidth - margin - 90, 88);
      };

      const drawTableHeader = (context: CanvasRenderingContext2D, y: number) => {
        context.font = 'bold 18px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
        let x = margin;
        columns.forEach(column => {
          strokeRect(context, x, y, column.width, 42, '#f1f5f9');
          context.fillStyle = '#334155';
          context.fillText(column.header, x + rowPadding, y + 12);
          x += column.width;
        });
        return y + 42;
      };

      const addCanvasToPdf = (canvas: HTMLCanvasElement, pageIndex: number) => {
        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, 210, 297);
      };

      let pageIndex = 0;
      let page = createCanvasPage();
      drawHeader(page.context, pageIndex + 1);
      let y = drawTableHeader(page.context, 128);

      rows.forEach(row => {
        page.context.font = '17px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
        const wrappedCells = row.map((value, index) => wrapText(page.context, value, columns[index].width - rowPadding * 2));
        const rowHeight = Math.max(52, Math.max(...wrappedCells.map(lines => lines.length)) * 24 + rowPadding * 2);

        if (y + rowHeight > pageHeight - margin) {
          addCanvasToPdf(page.canvas, pageIndex);
          pageIndex += 1;
          page = createCanvasPage();
          drawHeader(page.context, pageIndex + 1);
          y = drawTableHeader(page.context, 128);
        }

        let x = margin;
        wrappedCells.forEach((lines, index) => {
          strokeRect(page.context, x, y, columns[index].width, rowHeight);
          page.context.fillStyle = '#0f172a';
          lines.forEach((line, lineIndex) => {
            page.context.fillText(line, x + rowPadding, y + rowPadding + lineIndex * 24);
          });
          x += columns[index].width;
        });
        y += rowHeight;
      });

      if (rows.length === 0) {
        strokeRect(page.context, margin, y, tableWidth, 58);
        page.context.fillStyle = '#475569';
        page.context.font = '18px "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif';
        page.context.fillText('依頼・確認候補はありません', margin + rowPadding, y + 18);
      }

      addCanvasToPdf(page.canvas, pageIndex);
      pdf.save(`残薬管理_依頼確認候補_${new Date().toISOString().slice(0, 10)}.pdf`);
      showNotice('PDFを出力しました');
    } catch (error) {
      console.error('Medicine PDF export failed:', error);
      alert('PDF出力に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setPdfExporting(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
          <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-teal-600 text-white">
                <Pill className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">残薬管理</h1>
                <p className="text-sm text-slate-500">さつきホーム 看護師用</p>
              </div>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-slate-700">パスコード</span>
                <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 focus-within:border-teal-600">
                  <KeyRound className="h-5 w-5 text-slate-400" />
                  <input
                    type="password"
                    inputMode="numeric"
                    value={passcode}
                    onChange={event => setPasscode(event.target.value)}
                    className="w-full border-0 bg-transparent text-lg outline-none"
                    autoFocus
                  />
                </div>
              </label>
              {authError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {authError}
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !passcode}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-4 py-3 font-semibold text-white transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {loading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <KeyRound className="h-5 w-5" />}
                入る
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <style>
        {`@media screen{.medicine-print{display:none}}@media print{.medicine-screen{display:none!important}.medicine-print{display:block!important}body{background:white}}`}
      </style>
      <div className="medicine-screen">
        <header className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-lg bg-teal-700 text-white">
                <Pill className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">残薬管理</h1>
                <p className="text-sm text-slate-500">入居者別の残薬・外部受診薬</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {saveNotice && <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-700">{saveNotice}</span>}
              <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1">
                <button
                  onClick={() => setViewMode('residents')}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${viewMode === 'residents' ? 'bg-teal-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  入居者別
                </button>
                <button
                  onClick={() => setViewMode('drugs')}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${viewMode === 'drugs' ? 'bg-teal-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                >
                  薬名マスター
                </button>
              </div>
              <Link to="/care" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
                <ArrowLeft className="h-4 w-4" />
                ケアトップ
              </Link>
              <Link to={`/admin/residents${selectedResident ? `?resident=${selectedResident.id}&detail=medications` : ''}`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
                <UsersRound className="h-4 w-4" />
                入居者管理
              </Link>
              <button onClick={() => loadSnapshot()} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                更新
              </button>
              <button onClick={exportRequestPdf} disabled={pdfExporting} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400">
                <Printer className={`h-4 w-4 ${pdfExporting ? 'animate-pulse' : ''}`} />
                {pdfExporting ? 'PDF作成中' : 'PDF'}
              </button>
              <button onClick={exportRequestCsv} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium hover:bg-slate-50">
                <FileDown className="h-4 w-4" />
                CSV
              </button>
              <button onClick={handleLogout} className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900">
                <LogOut className="h-4 w-4" />
                ロック
              </button>
            </div>
          </div>
        </header>

        <main className="mx-auto grid max-w-[1440px] gap-4 px-4 py-4 sm:px-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <aside className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <SummaryTile label="入居者" value={summary.activeResidents} />
              <SummaryTile label="薬" value={summary.activeMeds} />
              <SummaryTile label="往診依頼" value={summary.requestCount} tone="red" />
              <SummaryTile label="外部確認" value={summary.externalRequestCount} tone="blue" />
              <SummaryTile label="未確認" value={summary.unconfirmed} tone="amber" />
            </div>

            <RequestPanel
              title="往診依頼候補"
              description="往診医へ依頼する薬で、残薬が依頼ラインまたは次回往診目安を下回る薬"
              requestItems={homeVisitRequestItems}
              residentById={residentById}
              tone="red"
              emptyLabel="現時点ではありません"
              onSelect={residentId => {
                setSelectedResidentId(residentId);
                setViewMode('residents');
              }}
            />

            <RequestPanel
              title="外部受診薬の確認候補"
              description="外部受診①〜③の薬で、残薬確認が必要な薬"
              requestItems={externalRequestItems}
              residentById={residentById}
              tone="blue"
              emptyLabel="現時点ではありません"
              onSelect={residentId => {
                setSelectedResidentId(residentId);
                setViewMode('residents');
              }}
            />

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="入居者検索"
                    className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-teal-600"
                  />
                </div>
                <button
                  onClick={() => setResidentDraft(emptyResidentDraft(residents.length + 1))}
                  className="grid h-9 w-9 place-items-center rounded-lg bg-teal-700 text-white hover:bg-teal-800"
                  title="入居者追加"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <label className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={showInactive} onChange={event => setShowInactive(event.target.checked)} />
                終了分も表示
              </label>
              <div className="max-h-[calc(100vh-280px)] space-y-1 overflow-auto pr-1">
                {filteredResidents.map((resident, index) => {
                  const meds = evaluatedMedications.filter(item => item.resident_id === resident.id && item.active);
                  const requestCount = meds.filter(item => item.status === 'request' && !isExternalSource(item.prescription_source)).length;
                  const externalRequestCount = meds.filter(item => item.status === 'request' && isExternalSource(item.prescription_source)).length;
                  const unconfirmedCount = meds.filter(item => item.status === 'unconfirmed').length;
                  return (
                    <button
                      key={resident.id}
                      onClick={() => setSelectedResidentId(resident.id)}
                      className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                        selectedResident?.id === resident.id
                          ? 'border-teal-300 bg-teal-50'
                          : 'border-transparent hover:border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-2 font-medium">
                          <span className="grid h-6 w-6 flex-none place-items-center rounded-md bg-slate-100 text-[11px] font-bold text-slate-600">{index + 1}</span>
                          <span className="truncate">{resident.name}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          {requestCount > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">{requestCount}</span>}
                          {externalRequestCount > 0 && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800">{externalRequestCount}</span>}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                        <span className={hasResidentKana(resident) ? '' : 'font-semibold text-amber-700'}>{resident.kana || 'ふりがな未登録'}</span>
                        {unconfirmedCount > 0 && <span className="text-amber-700">未確認 {unconfirmedCount}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="min-w-0 space-y-4">
            {viewMode === 'drugs' ? (
              <DrugMasterPanel
                summaries={drugMasterRows}
                query={drugQuery}
                onQueryChange={setDrugQuery}
                totalMasterCount={drugMaster.length}
              />
            ) : selectedResident ? (
              <>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <UserRound className="h-5 w-5 text-teal-700" />
                        <h2 className="text-2xl font-bold">{selectedResident.name}</h2>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">
                        {selectedResident.kana || 'かな未設定'} / 居室 {selectedResident.room || '-'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setResidentDraft(residentToDraft(selectedResident))}
                        className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium hover:bg-slate-50"
                      >
                        <Save className="h-4 w-4" />
                        入居者編集
                      </button>
                      <button
                        onClick={() => setMedicationDraft(emptyMedicationDraft(selectedResident.id, selectedMedications.length * 10 + 10))}
                        className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-3 py-2 text-sm font-medium text-white hover:bg-teal-800"
                      >
                        <Plus className="h-4 w-4" />
                        薬追加
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-4">
                    <InfoBand icon={<CalendarClock className="h-4 w-4" />} label="最終往診" value={selectedResident.last_visit_date || '-'} />
                    <InfoBand icon={<CalendarClock className="h-4 w-4" />} label="次回往診目安" value={getNextVisitDate(selectedResident) || '-'} />
                    <InfoBand icon={<ClipboardList className="h-4 w-4" />} label="往診依頼" value={`${selectedMedications.filter(item => item.status === 'request' && !isExternalSource(item.prescription_source)).length}件`} />
                    <InfoBand icon={<ClipboardList className="h-4 w-4" />} label="外部確認" value={`${selectedMedications.filter(item => item.status === 'request' && isExternalSource(item.prescription_source)).length}件`} />
                  </div>
                </div>

                {selectedMedicationGroups.length > 0 && (
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">処方元・医療機関別</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedMedicationGroups.map(group => {
                        const hospitalColor = getHospitalColor(group.hospitalName);
                        return (
                          <span
                            key={group.key}
                            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold shadow-sm ${hospitalColor.strongBadgeClass}`}
                          >
                            {group.title}
                            <span className="rounded bg-white/20 px-1.5 py-0.5 text-xs">{group.medications.length}件</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <div className="max-h-[65vh] overflow-auto overscroll-contain">
                    <table className="w-full min-w-[1080px] text-sm">
                      <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs uppercase text-slate-500 shadow-sm">
                        <tr>
                          <th className="px-3 py-3">薬名</th>
                          <th className="px-3 py-3">医療機関</th>
                          <th className="px-3 py-3">服用</th>
                          <th className="px-3 py-3 text-right">残薬目安</th>
                          <th className="px-3 py-3 text-right">残日数</th>
                          <th className="px-3 py-3">本日残薬</th>
                          <th className="px-3 py-3">判定</th>
                          <th className="px-3 py-3 text-right">操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedMedicationGroups.map(group => (
                          <Fragment key={group.key}>
                            <MedicationGroupHeader group={group} />
                            {group.medications.map(medication => (
                              <MedicationRow
                                key={medication.id}
                                medication={medication}
                                stockValue={stockDrafts[medication.id] ?? String(medication.current_stock)}
                                onStockChange={value => setStockDrafts(prev => ({ ...prev, [medication.id]: value }))}
                                onSaveStock={() => saveStock(medication)}
                                onEdit={() => setMedicationDraft(medicationToDraft(medication))}
                                onArchive={() => archiveMedication(medication)}
                              />
                            ))}
                          </Fragment>
                        ))}
                        {selectedMedications.length === 0 && (
                          <tr>
                            <td className="px-3 py-8 text-center text-slate-500" colSpan={8}>
                              薬情報がありません
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
                入居者を選択してください
              </div>
            )}
          </section>
        </main>

        {residentDraft && (
          <ResidentModal
            draft={residentDraft}
            onChange={setResidentDraft}
            onClose={() => setResidentDraft(null)}
            onSave={saveResident}
            onArchive={
              residentDraft.id
                ? () => {
                  const target = residents.find(resident => resident.id === residentDraft.id);
                  if (target) void archiveResident(target);
                }
                : undefined
            }
          />
        )}

        {medicationDraft && (
          <MedicationModal
            draft={medicationDraft}
            drugMaster={drugMaster}
            onChange={setMedicationDraft}
            onClose={() => setMedicationDraft(null)}
            onSave={saveMedication}
          />
        )}
      </div>

      <PrintRequestList requestItems={requestItems} residentById={residentById} />
    </div>
  );
};

const SummaryTile = ({ label, value, tone = 'slate' }: { label: string; value: number; tone?: 'slate' | 'red' | 'amber' | 'blue' }) => {
  const toneClass =
    tone === 'red'
      ? 'text-red-700 bg-red-50'
      : tone === 'amber'
        ? 'text-amber-700 bg-amber-50'
        : tone === 'blue'
          ? 'text-sky-800 bg-sky-50'
          : 'text-slate-800 bg-white';
  return (
    <div className={`rounded-lg border border-slate-200 p-3 ${toneClass}`}>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
};

const InfoBand = ({ icon, label, value }: { icon: ReactNode; label: string; value: string }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <div className="flex items-center gap-2 text-xs font-medium text-slate-500">{icon}{label}</div>
    <div className="mt-1 font-semibold text-slate-900">{value}</div>
  </div>
);

const RequestPanel = ({
  title,
  description,
  requestItems,
  residentById,
  tone,
  emptyLabel,
  onSelect
}: {
  title: string;
  description: string;
  requestItems: MedicationEvaluation[];
  residentById: Map<string, MedicineResident>;
  tone: 'red' | 'blue';
  emptyLabel: string;
  onSelect: (residentId: string) => void;
}) => {
  const styles = tone === 'red'
    ? {
      border: 'border-red-200',
      headerBorder: 'border-red-100',
      title: 'text-red-800',
      description: 'text-red-700',
      count: 'bg-red-50 text-red-700',
      item: 'border-red-100 bg-red-50 hover:border-red-200 hover:bg-red-100',
      itemText: 'text-red-950',
      subText: 'text-red-800'
    }
    : {
      border: 'border-sky-200',
      headerBorder: 'border-sky-100',
      title: 'text-sky-900',
      description: 'text-sky-800',
      count: 'bg-sky-50 text-sky-800',
      item: 'border-sky-100 bg-sky-50 hover:border-sky-200 hover:bg-sky-100',
      itemText: 'text-sky-950',
      subText: 'text-sky-800'
    };

  return (
  <div className={`rounded-lg border ${styles.border} bg-white`}>
    <div className={`border-b ${styles.headerBorder} px-3 py-2`}>
      <div className="flex items-center justify-between gap-2">
        <div className={`flex items-center gap-2 text-sm font-bold ${styles.title}`}>
          <AlertTriangle className="h-4 w-4" />
          {title}
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${styles.count}`}>
          {requestItems.length}件
        </span>
      </div>
      <p className={`mt-1 text-xs leading-5 ${styles.description}`}>{description}</p>
    </div>
    <div className="max-h-56 space-y-2 overflow-auto p-3">
      {requestItems.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-2 text-xs font-medium text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {emptyLabel}
        </div>
      ) : (
        requestItems.map(item => {
          const resident = residentById.get(item.resident_id);
          return (
            <button
              key={item.id}
              onClick={() => onSelect(item.resident_id)}
              className={`w-full rounded-lg border p-2 text-left transition ${styles.item}`}
            >
              <div className={`text-sm font-semibold ${styles.itemText}`}>{resident?.name ?? '-'} / {item.drug_name}</div>
              {isExternalSource(item.prescription_source) && (
                <div className={`mt-1 text-xs ${styles.subText}`}>{formatMedicationVisitSummary(item)}</div>
              )}
              <div className={`mt-1 text-xs ${styles.subText}`}>
                残 {item.daysLeft === null ? '-' : `${formatNumber(item.daysLeft, 1)}日`} / {formatNumber(item.estimatedStock, 1)}{item.stock_unit}
              </div>
            </button>
          );
        })
      )}
    </div>
  </div>
  );
};

const DrugMasterPanel = ({
  summaries,
  query,
  onQueryChange,
  totalMasterCount
}: {
  summaries: DrugMasterSummary[];
  query: string;
  onQueryChange: (value: string) => void;
  totalMasterCount: number;
}) => (
  <div className="space-y-4">
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Pill className="h-5 w-5 text-teal-700" />
            <h2 className="text-2xl font-bold">薬名マスター</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            薬追加時に選択する薬名候補
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end">
          <div className="text-sm text-slate-500">全{totalMasterCount}件 / 表示{summaries.length}件</div>
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={event => onQueryChange(event.target.value)}
              placeholder="薬名・入居者で検索"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-teal-600"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <div className="max-h-[70vh] overflow-auto overscroll-contain">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="sticky top-0 z-30 bg-slate-100 text-left text-xs uppercase text-slate-500 shadow-sm">
            <tr>
              <th className="px-3 py-3">薬名</th>
              <th className="px-3 py-3">標準単位</th>
              <th className="px-3 py-3 text-right">使用中</th>
              <th className="px-3 py-3">使用中の入居者</th>
            </tr>
          </thead>
          <tbody>
            {summaries.map(summary => (
              <tr key={summary.id} className="border-t border-slate-100 align-top">
                <td className="px-3 py-3">
                  <div className="font-semibold text-slate-900">{summary.drugName}</div>
                </td>
                <td className="px-3 py-3 text-slate-700">{summary.defaultUnit || '-'}</td>
                <td className="px-3 py-3 text-right">
                  <div className="font-semibold text-slate-900">{summary.residentCount}名</div>
                  <div className="text-xs text-slate-500">{summary.activeMedicationCount}件</div>
                </td>
                <td className="px-3 py-3 text-slate-600">
                  {summary.residentNames.length ? (
                    <div className="max-w-[360px] text-xs leading-5">
                      {summary.residentNames.slice(0, 8).join('、')}
                      {summary.residentNames.length > 8 ? ` ほか${summary.residentNames.length - 8}名` : ''}
                    </div>
                  ) : (
                    <span className="text-slate-400">使用中なし</span>
                  )}
                </td>
              </tr>
            ))}
            {summaries.length === 0 && (
              <tr>
                <td className="px-3 py-8 text-center text-slate-500" colSpan={4}>
                  該当する薬がありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const MedicationGroupHeader = ({ group }: { group: MedicationGroup }) => {
  const sourceMeta = prescriptionSourceMeta[group.source];
  const hospitalColor = getHospitalColor(group.hospitalName);
  return (
    <tr>
      <td colSpan={8} className="border-t border-slate-200 bg-white px-3 py-0">
        <div className={`my-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 ${hospitalColor.headerClass}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-md px-2.5 py-1 text-sm font-bold shadow-sm ${sourceMeta.groupBadgeClass}`}>
              {prescriptionSourceMeta[group.source].label}
            </span>
            <span className={`rounded-md px-2.5 py-1 text-sm font-bold shadow-sm ${hospitalColor.strongBadgeClass}`}>
              {group.hospitalName || '医療機関未設定'}
            </span>
            {group.externalLabel && <span className="rounded-md bg-white px-2.5 py-1 text-sm font-bold text-slate-700 ring-1 ring-slate-300">{group.externalLabel}</span>}
            <span className="text-sm font-semibold">{group.medications.length}件</span>
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs font-bold">
            {group.requestCount > 0 && <span className="rounded-full bg-red-600 px-2 py-1 text-white">要対応 {group.requestCount}</span>}
            {group.unconfirmedCount > 0 && <span className="rounded-full bg-amber-300 px-2 py-1 text-amber-950">未確認 {group.unconfirmedCount}</span>}
            {group.endedCount > 0 && <span className="rounded-full bg-slate-700 px-2 py-1 text-white">終了日経過 {group.endedCount}</span>}
          </div>
        </div>
      </td>
    </tr>
  );
};

const MedicationRow = ({
  medication,
  stockValue,
  onStockChange,
  onSaveStock,
  onEdit,
  onArchive
}: {
  medication: MedicationEvaluation;
  stockValue: string;
  onStockChange: (value: string) => void;
  onSaveStock: () => void;
  onEdit: () => void;
  onArchive: () => void;
}) => {
  const statusMeta = {
    request: ['要依頼', 'bg-red-100 text-red-800'],
    ok: ['在庫あり', 'bg-emerald-100 text-emerald-800'],
    manual: ['手動確認', 'bg-sky-100 text-sky-800'],
    unconfirmed: ['未確認', 'bg-amber-100 text-amber-800'],
    inactive: ['終了', 'bg-slate-100 text-slate-600'],
    ended: ['終了日経過', 'bg-slate-200 text-slate-700']
  }[medication.status];
  const source = normalizePrescriptionSource(medication.prescription_source);
  const sourceMeta = prescriptionSourceMeta[source];
  const hospitalName = getHospitalName(medication);
  const hospitalColor = getHospitalColor(hospitalName);

  return (
    <tr className={`border-l-4 border-t border-slate-100 align-top ${sourceMeta.leftBorderClass} ${sourceMeta.rowClass}`}>
      <td className="px-3 py-3">
        <div className="font-semibold text-slate-900">{medication.drug_name}</div>
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          <span className="rounded-md bg-slate-200 px-2.5 py-1 font-semibold text-slate-700">{medicationTypeLabels[medication.medication_type]}</span>
          {medication.days_supply && <span className="rounded-md bg-slate-200 px-2.5 py-1 font-semibold text-slate-700">{medication.days_supply}日分</span>}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="space-y-1.5">
          <span className={`inline-flex rounded-md px-2.5 py-1 text-xs font-bold shadow-sm ${hospitalColor.badgeClass}`}>
            {hospitalName}
          </span>
          <div className="flex flex-wrap gap-1.5 text-xs">
            <span className={`rounded-md px-2.5 py-1 font-bold shadow-sm ${sourceMeta.badgeClass}`}>
              {sourceMeta.label}
            </span>
            {medication.external_label && (
              <span className="rounded-md bg-white px-2.5 py-1 font-bold text-slate-700 ring-1 ring-slate-300">
                {medication.external_label}
              </span>
            )}
            {medication.external_visit_type && (
              <span className={`rounded-md px-2.5 py-1 font-bold shadow-sm ${sourceMeta.badgeClass}`}>
                {externalVisitTypeLabels[medication.external_visit_type]}
              </span>
            )}
            {medication.external_end_date && (
              <span className="rounded-md bg-white px-2.5 py-1 font-bold text-slate-700 ring-1 ring-slate-300">
                終了 {medication.external_end_date}
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-slate-600">
        <div>{medication.dosage_text || '-'}</div>
        <div className="mt-1 text-xs">{medication.timing_text || '-'}</div>
        {medication.temporary_dates_text && <div className="mt-1 text-xs text-rose-700">{medication.temporary_dates_text}</div>}
      </td>
      <td className="px-3 py-3 text-right">
        {medication.stock_confirmed ? (
          <>
            <div className="font-semibold">{formatNumber(medication.estimatedStock, 1)}{medication.stock_unit}</div>
            <div className="text-xs text-slate-500">基準 {formatNumber(medication.current_stock, 1)} / {medication.elapsedDays}日</div>
          </>
        ) : (
          <span className="text-slate-400">未保存</span>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        {medication.daysLeft === null ? '-' : `${formatNumber(medication.daysLeft, 1)}日`}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            step="0.1"
            value={stockValue}
            onChange={event => onStockChange(event.target.value)}
            className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-right outline-none focus:border-teal-600"
          />
          <span className="text-sm text-slate-500">{medication.stock_unit}</span>
          <button onClick={onSaveStock} className="rounded-lg bg-teal-700 px-2 py-1 text-xs font-semibold text-white hover:bg-teal-800">
            保存
          </button>
        </div>
        <div className="mt-1 text-xs text-slate-500">{formatDateTime(medication.stock_saved_at)}</div>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusMeta[1]}`}>{statusMeta[0]}</span>
        <div className="mt-1 text-xs text-slate-500">1日 {formatNumber(medication.daily_use, 2)}{medication.stock_unit}</div>
      </td>
      <td className="px-3 py-3 text-right">
        <div className="flex justify-end gap-2">
          <button onClick={onEdit} className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-50">
            編集
          </button>
          <button onClick={onArchive} className="grid h-8 w-8 place-items-center rounded-lg border border-slate-300 text-slate-500 hover:bg-slate-50" title="終了">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const ResidentModal = ({
  draft,
  onChange,
  onClose,
  onSave,
  onArchive
}: {
  draft: ResidentDraft;
  onChange: (draft: ResidentDraft) => void;
  onClose: () => void;
  onSave: () => void;
  onArchive?: () => void;
}) => (
  <Modal title={draft.id ? '入居者編集' : '入居者追加'} onClose={onClose}>
    <div className="grid gap-3 sm:grid-cols-2">
      <TextField label="氏名" value={draft.name} onChange={value => onChange({ ...draft, name: value })} />
      <TextField label="かな" value={draft.kana} onChange={value => onChange({ ...draft, kana: value })} />
      <TextField label="居室" value={draft.room} onChange={value => onChange({ ...draft, room: value })} />
      <NumberField label="表示順" value={draft.display_order} onChange={value => onChange({ ...draft, display_order: value })} />
      <DateField label="最終往診日" value={draft.last_visit_date} onChange={value => onChange({ ...draft, last_visit_date: value })} />
      <DateField label="次回往診日" value={draft.next_visit_date} onChange={value => onChange({ ...draft, next_visit_date: value })} />
      <NumberField label="往診間隔（日）" value={draft.visit_interval_days} onChange={value => onChange({ ...draft, visit_interval_days: value })} />
      <TextField label="メモ" value={draft.notes} onChange={value => onChange({ ...draft, notes: value })} />
    </div>
    <div className="mt-5 flex justify-between gap-2">
      {onArchive ? (
        <button onClick={onArchive} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50">
          非表示
        </button>
      ) : <span />}
      <div className="flex gap-2">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">閉じる</button>
        <button onClick={onSave} className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800">保存</button>
      </div>
    </div>
  </Modal>
);

const MedicationModal = ({
  draft,
  drugMaster,
  onChange,
  onClose,
  onSave
}: {
  draft: MedicationDraft;
  drugMaster: MedicineDrug[];
  onChange: (draft: MedicationDraft) => void;
  onClose: () => void;
  onSave: () => void;
}) => {
  const selectedMasterDrug = drugMaster.find(drug => drug.drug_name === draft.drug_name);
  const showCustomDrugInput = draft.use_custom_drug_name || (!selectedMasterDrug && Boolean(draft.drug_name)) || drugMaster.length === 0;
  const drugSelectValue = showCustomDrugInput ? '__custom__' : selectedMasterDrug?.id ?? '';
  const externalSourceSelected = draft.prescription_source !== 'home_visit';

  return (
    <Modal title={draft.id ? '薬情報編集' : '薬追加'} onClose={onClose}>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="mb-1 block text-sm font-medium text-slate-700">薬名マスター</span>
          <select
            value={drugSelectValue}
            onChange={event => {
              const value = event.target.value;
              if (value === '__custom__') {
                onChange({ ...draft, drug_name: '', use_custom_drug_name: true });
                return;
              }
              const drug = drugMaster.find(item => item.id === value);
              onChange({
                ...draft,
                drug_name: drug?.drug_name ?? '',
                use_custom_drug_name: false,
                stock_unit: drug?.default_unit || draft.stock_unit
              });
            }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
          >
            <option value="">選択してください</option>
            {drugMaster.map(drug => <option key={drug.id} value={drug.id}>{drug.drug_name}</option>)}
            <option value="__custom__">候補にない薬名を入力</option>
          </select>
        </label>
        {showCustomDrugInput && (
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-slate-700">新しい薬名</span>
            <input
              value={draft.drug_name}
              onChange={event => onChange({ ...draft, drug_name: event.target.value, use_custom_drug_name: true })}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
            />
            <span className="mt-1 block text-xs text-slate-500">保存後、薬名マスターの候補に追加されます</span>
          </label>
        )}
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">区分</span>
          <select
            value={draft.medication_type}
            onChange={event => onChange({ ...draft, medication_type: event.target.value as MedicationType })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
          >
            <option value="normal">通常</option>
            <option value="temporary">一時</option>
            <option value="prn">頓服</option>
            <option value="custom">その他</option>
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">処方元</span>
          <select
            value={draft.prescription_source}
            onChange={event => {
              const source = normalizePrescriptionSource(event.target.value);
              onChange({
                ...draft,
                prescription_source: source,
                external_visit_type: source === 'home_visit' ? '' : draft.external_visit_type || 'regular',
                external_hospital_name: source === 'home_visit' ? '' : draft.hospital_name,
                external_label: source === 'home_visit' ? '' : draft.external_label,
                external_end_date: source === 'home_visit' ? '' : draft.external_end_date
              });
            }}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
          >
            <option value="home_visit">往診医</option>
            <option value="external_1">外部受診①</option>
            <option value="external_2">外部受診②</option>
            <option value="external_3">外部受診③</option>
          </select>
        </label>
        <TextField label="医療機関名" value={draft.hospital_name} onChange={value => onChange({ ...draft, hospital_name: value, external_hospital_name: externalSourceSelected ? value : '' })} />
        {externalSourceSelected && (
          <>
            <TextField label="外部受診ラベル" value={draft.external_label} onChange={value => onChange({ ...draft, external_label: value })} />
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700">外部受診区分</span>
              <select
                value={draft.external_visit_type}
                onChange={event => {
                  const visitType = event.target.value as ExternalVisitType | '';
                  onChange({
                    ...draft,
                    external_visit_type: visitType,
                    external_end_date: visitType === 'temporary' ? draft.external_end_date : ''
                  });
                }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
              >
                <option value="">未設定</option>
                <option value="regular">定期</option>
                <option value="temporary">臨時</option>
              </select>
            </label>
            {draft.external_visit_type === 'temporary' && (
              <DateField label="臨時終了日" value={draft.external_end_date} onChange={value => onChange({ ...draft, external_end_date: value })} />
            )}
          </>
        )}
        <TextField label="服用量" value={draft.dosage_text} onChange={value => onChange({ ...draft, dosage_text: value })} />
        <TextField label="服用時間" value={draft.timing_text} onChange={value => onChange({ ...draft, timing_text: value })} />
        <TextField label="服用日" value={draft.temporary_dates_text} onChange={value => onChange({ ...draft, temporary_dates_text: value })} />
        <NumberField label="1日使用量" value={draft.daily_use} step="0.01" onChange={value => onChange({ ...draft, daily_use: value })} />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700">残薬単位</span>
          <select
            value={draft.stock_unit}
            onChange={event => onChange({ ...draft, stock_unit: event.target.value })}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
          >
            {STOCK_UNITS.map(unit => <option key={unit} value={unit}>{unit}</option>)}
          </select>
        </label>
        <NumberField label="依頼ライン（日）" value={draft.order_threshold_days} onChange={value => onChange({ ...draft, order_threshold_days: value })} />
        <TextField label="処方日数" value={draft.days_supply} onChange={value => onChange({ ...draft, days_supply: value.replace(/[^\d]/g, '') })} />
        <TextField label="メモ" value={draft.notes} onChange={value => onChange({ ...draft, notes: value })} />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50">閉じる</button>
        <button onClick={onSave} className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800">保存</button>
      </div>
    </Modal>
  );
};

const Modal = ({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4">
    <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h3 className="text-lg font-bold">{title}</h3>
        <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const TextField = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
    <input
      value={value}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
    />
  </label>
);

const NumberField = ({
  label,
  value,
  step = '1',
  onChange
}: {
  label: string;
  value: number;
  step?: string;
  onChange: (value: number) => void;
}) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
    <input
      type="number"
      min="0"
      step={step}
      value={value}
      onChange={event => onChange(normalizeNumber(event.target.value))}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
    />
  </label>
);

const DateField = ({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) => (
  <label className="block">
    <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
    <input
      type="date"
      value={value}
      onChange={event => onChange(event.target.value)}
      className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-teal-600"
    />
  </label>
);

const PrintRequestList = ({
  requestItems,
  residentById
}: {
  requestItems: MedicationEvaluation[];
  residentById: Map<string, MedicineResident>;
}) => (
  <div className="medicine-print p-8 text-slate-900">
    <h1 className="text-2xl font-bold">依頼・確認候補</h1>
    <p className="mt-1 text-sm">出力日: {new Date().toLocaleDateString('ja-JP')}</p>
    <table className="mt-6 w-full border-collapse text-sm">
      <thead>
        <tr>
          {['区分', '入居者', '処方元', '医療機関', '薬名', '残薬目安', '残日数', '服用量', '服用時間'].map(header => (
            <th key={header} className="border border-slate-300 bg-slate-100 px-2 py-2 text-left">{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {requestItems.map(item => {
          const resident = residentById.get(item.resident_id);
          return (
            <tr key={item.id}>
              <td className="border border-slate-300 px-2 py-2">{isExternalSource(item.prescription_source) ? '外部確認' : '往診依頼'}</td>
              <td className="border border-slate-300 px-2 py-2">{resident?.name ?? '-'}</td>
              <td className="border border-slate-300 px-2 py-2">{formatMedicationSource(item)}</td>
              <td className="border border-slate-300 px-2 py-2">{formatMedicationHospital(item)}</td>
              <td className="border border-slate-300 px-2 py-2">{item.drug_name}</td>
              <td className="border border-slate-300 px-2 py-2">{formatNumber(item.estimatedStock, 1)}{item.stock_unit}</td>
              <td className="border border-slate-300 px-2 py-2">{item.daysLeft === null ? '-' : `${formatNumber(item.daysLeft, 1)}日`}</td>
              <td className="border border-slate-300 px-2 py-2">{item.dosage_text || '-'}</td>
              <td className="border border-slate-300 px-2 py-2">{item.timing_text || '-'}</td>
            </tr>
          );
        })}
        {requestItems.length === 0 && (
          <tr>
            <td className="border border-slate-300 px-2 py-6 text-center" colSpan={9}>依頼・確認候補はありません</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default MedicinePage;
